import { useState, useEffect } from 'react';

import { getPersistentId } from '@/modules/activityTracker/helpers';
import { type BranchDeeplinkProps } from '@/modules/externalTracking/types.ts';
import { useBranch } from '@/modules/externalTracking/useBranch.ts';
import { getLogger } from '@/modules/observability/logging';

export function useBranchDeepLink({
  fallback,
  appPath,
  stage = '',
  referer = '',
  feature = 'organic',
  channel = 'website',
  campaign = '',
  referrerSource,
  excludeDeeplinkAttribution,
  desktopDeepView,
}: BranchDeeplinkProps) {
  const { getBranch } = useBranch();

  const [deepLink, setDeepLink] = useState(fallback);

  useEffect(() => {
    async function initDeepLink() {
      const branch = await getBranch();
      const persistentId = await getPersistentId();

      if (!branch) {
        return false;
      }

      window.branch.link(
        {
          channel,
          stage,
          feature,
          campaign,
          data: {
            referring_user: referer,
            referrerSource,
            referrerPersistentIdentifier: persistentId,
            $deeplink_path: appPath,
            $desktop_url: desktopDeepView ? undefined : fallback,
            $desktop_deepview: desktopDeepView,
            $canonical_url: fallback,
            $original_url: fallback,
            $deeplink_no_attribution: excludeDeeplinkAttribution,
          },
        },

        (err, link) => {
          if (link) {
            return setDeepLink(link);
          }
          getLogger().warn('Branch deep link creation failed', {
            campaign,
            feature,
            stage,
            referrerSource,
            error: String(err),
          });
        }
      );
    }

    initDeepLink();
  }, [
    appPath,
    campaign,
    stage,
    feature,
    referer,
    channel,
    fallback,
    referrerSource,
    excludeDeeplinkAttribution,
    desktopDeepView,
  ]);

  return deepLink;
}
